package com.siriusxm.pia

import com.siriusxm.pia.components.shadowBorder
import org.jetbrains.compose.web.css.*

/**
 * Constants for the SiriusXM UI
 */
object SXMUI {
    val mainHeaderBackgroundColor by variable<CSSColorValue>()

    val mainLayoutBackgroundColor by variable<CSSColorValue>()
    val containerHeaderBackgroundColor by variable<CSSColorValue>()
    val containerContentBackgroundColor by variable<CSSColorValue>()
    val borderDividerDefault by variable<CSSColorValue>()

    val linkColor by variable<CSSColorValue>()
    val linkHoverColor by variable<CSSColorValue>()
    val textColor by variable<CSSColorValue>()
    val subtleTextColor by variable<CSSColorValue>()
    val disabledTextColor by variable<CSSColorValue>()
    val selectionColor by variable<CSSColorValue>()
    val selectionBackgroundColor by variable<CSSColorValue>()
    val tabsBorder by variable<CSSColorValue>()

    val brandColor by variable<CSSColorValue>()
    val highlightColor by variable<CSSColorValue>()

    val defaultDivider by variable<CSSColorValue>()

    val buttonBackgroundNormal by variable<CSSColorValue>()
    val buttonBackgroundNormalHover by variable<CSSColorValue>()
    val buttonForegroundNormal by variable<CSSColorValue>()
    val buttonForegroundNormalHover by variable<CSSColorValue>()
    val buttonBorderNormal by variable<CSSColorValue>()
    val buttonBackgroundPrimary by variable<CSSColorValue>()
    val buttonBackgroundPrimaryHover by variable<CSSColorValue>()
    val buttonForegroundPrimary by variable<CSSColorValue>()

    // message boxes
    val messageInfoBorder by variable<CSSColorValue>()
    val messageInfoBackground by variable<CSSColorValue>()
    val messageInfoForeground by variable<CSSColorValue>()
    val messageSuccessBorder by variable<CSSColorValue>()
    val messageSuccessBackground by variable<CSSColorValue>()
    val messageSuccessForeground by variable<CSSColorValue>()

    val messageWarnBorder by variable<CSSColorValue>()
    val messageWarnBackground by variable<CSSColorValue>()
    val messageWarnForeground by variable<CSSColorValue>()

    val messageAlertBorder by variable<CSSColorValue>()
    val messageAlertBackground by variable<CSSColorValue>()
    val messageAlertForeground by variable<CSSColorValue>()

    val goColor by variable<CSSColorValue>()
    val stopColor by variable<CSSColorValue>()

    // Solid status accent colors for status dots/badges (completed / failed / in-progress). Saturated
    // enough to read on both light and dark surfaces, so they are set once and not re-themed.
    val statusSuccessColor by variable<CSSColorValue>()
    val statusErrorColor by variable<CSSColorValue>()
    val statusPendingColor by variable<CSSColorValue>()

    // Accent for tool-call / tool-result content (e.g. the chat transcript trace). A saturated violet
    // distinct from the brand and status colors; readable on both themes, so set once.
    val toolAccentColor by variable<CSSColorValue>()

    val smallFontSize by variable<CSSNumeric>()

    /**
     * The global box radius amount
     */
    val boxRadius by variable<CSSNumeric>()
    val boxShadowColor by variable<CSSColorValue>()

    val darkBackgroundColor by variable<CSSColorValue>()

    // Background for the floating global audio player bar. A deep blue/purple that reads as a
    // distinct floating surface on both themes, so it is set once and not re-themed.
    val playerBackgroundColor by variable<CSSColorValue>()
    val playerForegroundColor by variable<CSSColorValue>()
    val playerDrawerBackgroundColor by variable<CSSColorValue>()
    val playerDrawerMutedColor by variable<CSSColorValue>()
    val playerDrawerLinkColor by variable<CSSColorValue>()
    val playerDrawerDividerColor by variable<CSSColorValue>()
    val playerMutedColor by variable<CSSColorValue>()
    val playerDividerColor by variable<CSSColorValue>()
    val playerTrackColor by variable<CSSColorValue>()
    val playerAccentColor by variable<CSSColorValue>()
    val playerControlSurface by variable<CSSColorValue>()
    val playerWarningAccent by variable<CSSColorValue>()

    val selectionControlSurface by variable<CSSColorValue>()

    val pillBackgroundColor by variable<CSSColorValue>()
    val pillForegroundColor by variable<CSSColorValue>()

}

object AppStylesheet : StyleSheet() {
    init {
        val brandColor = rgb(27, 17, 122)
        universal style {
            SXMUI.mainHeaderBackgroundColor(Color.black)
            SXMUI.mainLayoutBackgroundColor(Color("#f2f3f3"))
            SXMUI.borderDividerDefault(Color("#dddddd"))
            SXMUI.containerHeaderBackgroundColor(Color("#fafafa"))
            SXMUI.containerContentBackgroundColor(Color("#ffffff"))

            SXMUI.tabsBorder(Color("#aab7b8"))

            SXMUI.textColor(rgb(22, 25, 31))
            SXMUI.subtleTextColor(rgb(84, 91, 100))
            SXMUI.disabledTextColor(Color("#aab7b8"))

            SXMUI.brandColor(brandColor)
            SXMUI.highlightColor(Color("#C4169C"))
            SXMUI.selectionColor(SXMUI.textColor.value())
            SXMUI.selectionBackgroundColor(Color("#EBF4FF"))
            SXMUI.defaultDivider(SXMUI.borderDividerDefault.value())

            SXMUI.smallFontSize((0.9).cssRem)

            SXMUI.buttonBackgroundNormal(Color.white)
            SXMUI.buttonBackgroundNormalHover(Color("#eaeded"))
            SXMUI.buttonForegroundNormal(Color("#545b64"))
            SXMUI.buttonForegroundNormalHover(Color("#16191f"))
            SXMUI.buttonBorderNormal(Color("#545b64"))
            SXMUI.buttonBackgroundPrimary(brandColor)
            SXMUI.buttonBackgroundPrimaryHover(Color("#0051E6"))
            SXMUI.buttonForegroundPrimary(Color.white)

            SXMUI.linkColor(brandColor)
            SXMUI.linkHoverColor(SXMUI.buttonBackgroundPrimaryHover.value())

            SXMUI.messageInfoBorder(Color("#ccebff"))
            SXMUI.messageInfoBackground(Color("#f1faff"))
            SXMUI.messageInfoForeground(rgb(21, 87, 36))

            SXMUI.messageSuccessBorder(rgb(195, 230, 203))
            SXMUI.messageSuccessBackground(rgb(212, 237, 218))
            SXMUI.messageSuccessForeground(rgb(21, 87, 36))

            SXMUI.messageWarnBorder(rgb(255, 234, 167))
            SXMUI.messageWarnBackground(rgb(255, 243, 205))
            SXMUI.messageWarnForeground(rgb(133, 100, 4))

            SXMUI.messageAlertBorder(Color("#f7cac1"))
            SXMUI.messageAlertBackground(Color("#fdf3f1"))
            SXMUI.messageAlertForeground(rgb(114, 28, 36))

            SXMUI.goColor(Color("#009900"))
            SXMUI.stopColor(Color("#990000"))

            SXMUI.statusSuccessColor(Color("#28a745"))
            SXMUI.statusErrorColor(Color("#dc3545"))
            SXMUI.statusPendingColor(Color("#ffc107"))
            SXMUI.toolAccentColor(Color("#8957e5"))

            SXMUI.boxRadius(10.px)

            SXMUI.boxShadowColor(rgba(0, 0, 0, 0.2))

            SXMUI.darkBackgroundColor(rgb(16, 19, 21))

            // Deep blue/purple floating player surface (matches the player mockup).
            SXMUI.playerBackgroundColor(Color("#0c1230"))
            SXMUI.playerForegroundColor(Color.white)
            SXMUI.playerDrawerBackgroundColor(Color("#232943"))
            SXMUI.playerDrawerMutedColor(Color("#afc2ef"))
            SXMUI.playerDrawerLinkColor(Color("#99d4ff"))
            SXMUI.playerDrawerDividerColor(Color("#8295c1"))
            SXMUI.playerMutedColor(Color("#aeb5d0"))
            SXMUI.playerDividerColor(Color("#424b6b"))
            SXMUI.playerTrackColor(Color("#49526e"))
            SXMUI.playerAccentColor(Color("#0070ff"))
            SXMUI.playerControlSurface(Color.white)
            SXMUI.playerWarningAccent(Color("#d96b00"))

            SXMUI.selectionControlSurface(Color.white)

            SXMUI.pillBackgroundColor(SXMUI.textColor.value())
            SXMUI.pillBackgroundColor(SXMUI.containerHeaderBackgroundColor.value())

            boxSizing("border-box")
        }

        (".dark *") style {
            SXMUI.mainLayoutBackgroundColor(rgb(16, 19, 21))
            SXMUI.containerContentBackgroundColor(rgb(22, 26, 28))
            SXMUI.containerHeaderBackgroundColor(rgb(27, 31, 33))
            SXMUI.defaultDivider(rgb(48, 63, 72))
            SXMUI.borderDividerDefault(rgb(48, 63, 72))
            SXMUI.textColor(rgb(210, 214, 218))
            SXMUI.subtleTextColor(rgb(126, 141, 150))
            SXMUI.disabledTextColor(rgb(95, 105, 112))
            SXMUI.boxShadowColor(rgba(126, 141, 150, .4))
            SXMUI.selectionControlSurface(rgb(33, 44, 51))
            SXMUI.brandColor(rgb(32, 138, 255))
            SXMUI.linkColor(rgb(122, 163, 204))
            SXMUI.linkHoverColor(Color("#a8c4e4"))
            SXMUI.buttonBackgroundPrimary(Color("#364A6E"))
            SXMUI.selectionBackgroundColor(Color("#364A6E"))
            SXMUI.selectionColor(SXMUI.textColor.value())
            SXMUI.tabsBorder(rgb(48, 63, 72))

            SXMUI.buttonBackgroundNormal(Color("#2C2F33")) // very dark gray
            SXMUI.buttonForegroundNormal(Color("#D0D4D8")) // light gray text
            SXMUI.buttonBackgroundNormalHover(Color("#3A3E44"))
            SXMUI.buttonForegroundNormalHover(SXMUI.textColor.value())

            SXMUI.pillBackgroundColor(rgba(126, 141, 150, .3))

            SXMUI.messageInfoBorder(Color("#0073bb"))
            SXMUI.messageInfoBackground(SXMUI.messageInfoBorder.value())
            SXMUI.messageInfoForeground(SXMUI.textColor.value())
        }

        type("html") style {
            fontFamily("Helvetica Now", "Arial", "Helvetica", "sans-serif")
            fontSize(90.percent)
            letterSpacing((-0.028).em)
        }

        type("body") style {
            color(SXMUI.textColor.value())
        }

        type("A") style {
            color(SXMUI.linkColor.value())
        }

        (type("A") + hover) style {
            color(SXMUI.linkHoverColor.value())
        }

        "input, textarea, select" style {
            border(1.px, LineStyle.Solid, SXMUI.tabsBorder.value())
            borderRadius(10.px)
            padding((0.4).cssRem, (0.6).cssRem)
            backgroundColor(SXMUI.selectionControlSurface.value())
            color(SXMUI.textColor.value())
        }

        type("button") style {
            letterSpacing((-0.028).em)
        }

        "input[type=text], input[type=password], input[type=datetime-local], input[type=date], textarea, select" style {
            height(2.5.em)
            borderRadius(10.px)
            shadowBorder()
            border(1.px, LineStyle.Solid, Color.transparent)
        }

        (type("input[type=text].input-error")) style {
            outline("none")
            border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
        }

        (type("input[type=text].input-error") + focus) style {
            outline(SXMUI.messageAlertBorder.value(), LineStyle.Solid.name, 2.px)
        }

        // Only hide aggregator links on devices that support hover (desktop).
        // On touch-only devices (mobile), links are always visible.
        // Need to determine if there is a hover before we hide the link.
        media(CSSMediaQuery.Raw("(hover: hover)")) {
            ".aggregator-icon-link" style {
                display(DisplayStyle.None)
            }

            "*:hover > .aggregator-icon-link" style {
                display(DisplayStyle.Inline)
            }
        }
    }
}